/* eslint-disable no-underscore-dangle */
// @ts-nocheck
/**
 * Asset utilities
 *
 * This file now imports from the centralized chain config.
 * Token decimals are now properly configured per chain.
 */
import { AssetResponse, getAssetInfo } from ".";
import BTCLogo, {
  ReactComponent as BTCLogoComponent,
} from "../../assets/svg/assets/BTC.svg";
import ETHLogo, {
  ReactComponent as ETHLogoComponent,
} from "../../assets/svg/assets/ETH.svg";
import USDCLogo, {
  ReactComponent as USDCLogoComponent,
} from "../../assets/svg/assets/usdc.svg";
import USDTLogo, {
  ReactComponent as USDTLogoComponent,
} from "../../assets/svg/assets/usdt.svg";
import MONLogo from "../../assets/svg/assets/MON.svg";

import { CollateralAssetResponse } from "../../codegen-api";
import { getTokenDecimals, isMainnet, getNativeSymbol } from "../../config/chains";
import {
  AllCollaterals,
  DepositWithdrawCollaterals,
  addressToDepositCollateral,
  getCollateralERC20Addresses,
} from "../../constants/addresses";
import { ChainIdEnum, SOCKET_DEPOSIT_CHAINS } from "../../enums/chain";

const EUROCLogo = "https://assets.coingecko.com/coins/images/26045/standard/euro.png?1696525125";

export function getAssetLogo(
  asset?: AssetResponse | AllCollaterals
): string | undefined {
  // Normalize ANY symbol variant to its canonical display symbol FIRST, then look
  // up the logo by that canonical symbol. `getAssetShortName` is the single source
  // of truth for symbol canonicalization (WETH/mWETH → ETH, WBTC/mWBTC → BTC,
  // WNATIVE → chain-native symbol, MUSDC → USDC, XAU → GOLD …). Doing the lookup on
  // the RAW symbol previously matched only the exact hard-coded variants, so any
  // other form of the same asset (e.g. a spot market whose base isn't exactly
  // "WETH") fell through to the fallback icon. This makes the logo correct
  // everywhere `getAssetLogo` / `<TickerLogo>` is used — one reusable fix.
  const canonical = (
    getAssetShortName(asset as AssetResponse) ||
    (asset as string) ||
    ""
  ).toUpperCase();
  switch (canonical) {
    case "ETH":
      return ETHLogo;
    case "BTC":
      return BTCLogo;
    case "USDC":
      return USDCLogo;
    case "USDT":
      return USDTLogo;
    case "EUROC":
    case "EURC":
      return EUROCLogo;
    case "MON":
      return MONLogo;
    default: {
      const a = getAssetInfo(asset);
      return a?.logo;
    }
  }
}

export function getCollateralAssetLogo(asset?: AllCollaterals) {
  switch (asset) {
    case CollateralAssetResponse.Weth:
      return ETHLogoComponent;
    case CollateralAssetResponse.Usdt:
      return USDTLogoComponent;
    case CollateralAssetResponse.Usdc:
      return USDCLogoComponent;
    case DepositWithdrawCollaterals.NativeUsdc:
      return USDCLogoComponent;
    case CollateralAssetResponse.Wbtc:
      return BTCLogoComponent;
    case undefined:
      return undefined;
  }
  return undefined;
}

export function getCollateralAssetByAddress(
  collateralAddress: string
): AllCollaterals | undefined {
  const normalizedCollateralAddress = collateralAddress?.toLowerCase();
  const asset = collateralAddress
    ? (Object.keys(addressToDepositCollateral).find((key) =>
        addressToDepositCollateral[key as AllCollaterals].some(
          (address) => address.toLowerCase() === normalizedCollateralAddress
        )
      ) as AllCollaterals | undefined)
    : undefined;
  return asset;
}

export function getAssetToShow(
  asset?: AllCollaterals
): AllCollaterals | undefined {
  return asset;
}

export function getAssetName(asset: AssetResponse): string | undefined {
  return getAssetInfo(asset).name;
}

export function getAssetShortName(
  asset?: AssetResponse | CollateralAssetResponse,
  chainId?: ChainIdEnum
): string | undefined {
  // `WETH` is the wrapped-ETH ERC20 — always show as "ETH" regardless of which
  // token the chain uses for gas. `WNATIVE` is the wrapped-native token whose
  // display symbol does follow the chain (e.g. on Arc, native is USDC). Without
  // this split, the WETH collateral row was relabelled "USDC" on Arc because
  // `getNativeSymbol()` resolves to the chain's native, not to ETH.
  if (asset === "WETH" || asset === "mWETH" || asset === "MWETH") {
    return "ETH";
  }
  if (asset === "WNATIVE") {
    return getNativeSymbol(chainId);
  }
  if (asset === "WBTC" || asset === "mWBTC" || asset === "MWBTC") {
    return "BTC";
  }
  if (asset === "MUSDC") {
    return "USDC";
  }
  if (asset === "EURC") {
    return "EUROC";
  }
  if (asset === "XAU") {
    return "GOLD";
  }
  if (asset === "XAG") {
    return "SILVER";
  }
  if (
    asset === CollateralAssetResponse.Usdc &&
    (chainId === ChainIdEnum.ARBITRUM ||
      chainId === ChainIdEnum.ARBITRUM_TESTNET ||
      chainId === ChainIdEnum.OPTIMISM ||
      chainId === ChainIdEnum.OPTIMISM_TESTNET)
  ) {
    return "USDC.e";
  }
  if (
    asset === DepositWithdrawCollaterals.NativeUsdc &&
    chainId === undefined
  ) {
    return "USDC";
  }

  return asset;
}

// eslint-disable-next-line consistent-return
export function getCollateralPrecision(collateral: AllCollaterals) {
  switch (collateral) {
    case CollateralAssetResponse.Usdc:
    case CollateralAssetResponse.Usdt:
    case DepositWithdrawCollaterals.NativeUsdc:
    case "USDC" as any:
      return 2;
    case CollateralAssetResponse.Sdai:
    case CollateralAssetResponse.Weth:
    case "ETH" as any:
    case "MON" as any:
      return 4;
    case CollateralAssetResponse.Wbtc:
    case "BTC" as any:
      return 5;
    default:
      return 2;
  }
}

const testnetAllowedDeposits = Object.values(DepositWithdrawCollaterals).filter(
  (c) => c !== DepositWithdrawCollaterals.Usdt
);
const mainnetAllowedDeposits = Object.values(DepositWithdrawCollaterals).filter(
  (c) => c !== DepositWithdrawCollaterals.Usdt
);

// TODO: - Allow other collateral deposits when UI is ready
// Now uses centralized config to determine environment
const allDepositAssets = isMainnet()
  ? mainnetAllowedDeposits
  : testnetAllowedDeposits;

export const getCollateralAssets = (
  chainId?: ChainIdEnum,
  type?: "deposit" | "withdraw" | "transfer"
) => {
  let collaterals: AllCollaterals[] = Object.values(
    AllCollaterals
  ) as AllCollaterals[];
  // todo: remove this if statement once bridged usdc withdraw is available again
  if (chainId === ChainIdEnum.ARBITRUM && type === "withdraw") {
    collaterals = [
      DepositWithdrawCollaterals.NativeUsdc,
      DepositWithdrawCollaterals.Usdc,
      DepositWithdrawCollaterals.Weth,
    ] as DepositWithdrawCollaterals[];
  } else if (chainId && SOCKET_DEPOSIT_CHAINS.includes(chainId)) {
    collaterals = [
      DepositWithdrawCollaterals.Usdc,
      DepositWithdrawCollaterals.NativeUsdc,
      DepositWithdrawCollaterals.Weth,
    ] as DepositWithdrawCollaterals[];
  }

  collaterals = collaterals.filter((c) => c !== CollateralAssetResponse.Sdai);
  if (chainId && !SOCKET_DEPOSIT_CHAINS.includes(chainId)) {
    collaterals = collaterals.filter(
      (c) => c !== DepositWithdrawCollaterals.NativeUsdc
    );
  }

  // Filter type
  switch (type) {
    case "deposit":
      collaterals = collaterals.filter((c) =>
        allDepositAssets.includes(c as DepositWithdrawCollaterals)
      );
      break;
    case "transfer":
      if (chainId) {
        collaterals = collaterals.filter((c) => {
          const addresses = getCollateralERC20Addresses(c, chainId);
          return Boolean(addresses.l2 || addresses.l1);
        });
      }
      break;
    default:
      break;
  }

  // Only surface collaterals that are actually configured on this chain. Without
  // this, a chain-specific collateral (e.g. MON on Monad) would show up on every
  // chain's deposit/withdraw list with no token address behind it.
  if (chainId && (type === "deposit" || type === "withdraw")) {
    collaterals = collaterals.filter((c) => {
      const addresses = getCollateralERC20Addresses(c, chainId);
      return Boolean(addresses.l1 || addresses.l2);
    });
  }

  return collaterals;
};

/**
 * Get collateral asset decimals
 * Now uses centralized config
 */
const tokenDecimals = getTokenDecimals();

export const collateralAssetDecimals: {
  [collateral in AllCollaterals]: number;
} = {
  [CollateralAssetResponse.Usdc]: tokenDecimals.usdc,
  [DepositWithdrawCollaterals.NativeUsdc]: tokenDecimals.usdc,
  [CollateralAssetResponse.Usdt]: tokenDecimals.usdt,
  [CollateralAssetResponse.Weth]: tokenDecimals.weth,
  [CollateralAssetResponse.Wbtc]: tokenDecimals.wbtc,
  [CollateralAssetResponse.Sdai]: tokenDecimals.sdai,
  [DepositWithdrawCollaterals.Mon]: 18,
};
